import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './MyCourses.css';

const MyCourses = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [createdCourses, setCreatedCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const isInstructor = user?.role === 'instructor' || user?.role === 'admin' || user?.role === 'sub-admin';

  useEffect(() => {
    const fetchMyCourses = async () => {
      try {
        const enrolledRes = await api.get('/enrollments/my-courses');
        setEnrolledCourses(enrolledRes.data);

        if (isInstructor) {
          const createdRes = await api.get('/courses/instructor/my-courses');
          setCreatedCourses(createdRes.data);
        }
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMyCourses();
  }, [isInstructor]);

  const courses = enrolledCourses;

  const handleUnenroll = async (courseId) => {
    if (!window.confirm(t('myCourses.unenrollConfirm'))) {
      return;
    }

    try {
      await api.delete(`/enrollments/${courseId}`);
      setEnrolledCourses(enrolledCourses.filter((c) => c.course_id !== courseId));
    } catch (error) {
      console.error('Error unenrolling:', error);
      alert(t('myCourses.unenrollFailed'));
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  const completedCourses = courses.filter((course) => course.completed_at).length;
  const inProgressCourses = Math.max(courses.length - completedCourses, 0);

  return (
    <div className="page my-courses-page">
      <div className="container">
        <section className="my-courses-hero">
          <div className="hero-content">
            <p className="eyebrow">{isInstructor ? t('myCourses.instructorEyebrow') : t('myCourses.learnerEyebrow')}</p>
            <h1>{t('myCourses.title')}</h1>
            <p>{isInstructor ? t('myCourses.instructorSubtitle') : t('myCourses.learnerSubtitle')}</p>
          </div>
          <div className="hero-stats">
            {isInstructor && (
              <div>
                <span className="label">{t('myCourses.created')}</span>
                <strong>{createdCourses.length}</strong>
              </div>
            )}
            <div>
              <span className="label">{t('myCourses.enrolled')}</span>
              <strong>{courses.length}</strong>
            </div>
            <div>
              <span className="label">{t('myCourses.inProgress')}</span>
              <strong>{inProgressCourses}</strong>
            </div>
            <div>
              <span className="label">{t('myCourses.completed')}</span>
              <strong>{completedCourses}</strong>
            </div>
          </div>
        </section>

        {isInstructor && (
          <section className="created-courses-section">
            <div className="section-header">
              <h2>{t('myCourses.coursesCreated')}</h2>
              <Link to="/create-course" className="btn-primary">
                {t('myCourses.createNew')}
              </Link>
            </div>
            {createdCourses.length === 0 ? (
              <div className="empty-state elevated">
                <p>{t('myCourses.noCreatedYet')}</p>
                <Link to="/create-course" className="btn-primary">
                  {t('myCourses.createFirst')}
                </Link>
              </div>
            ) : (
              <div className="my-courses-grid">
                {createdCourses.map((course) => (
                  <article key={course.id} className="enrolled-card instructor-card">
                    <div className="card-top">
                      <span className="card-chip">{course.category_name || 'Course'}</span>
                      <span className={`status-pill ${course.status}`}>
                        {course.status === 'published' ? t('myCourses.published') : t('myCourses.draft')}
                      </span>
                    </div>
                    <h3>{course.title}</h3>
                    <p className="card-instructor">{course.enrollment_count || 0} {t('myCourses.studentsEnrolled')}</p>
                    <div className="card-actions">
                      <Link to={`/courses/${course.id}`} className="btn-primary">
                        {t('myCourses.viewCourse')}
                      </Link>
                      <Link to={`/edit-course/${course.id}`} className="btn-secondary">
                        {t('common.edit')}
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {isInstructor && <h2 className="section-title">{t('myCourses.enrolledSection')}</h2>}

        {courses.length === 0 ? (
          <div className="empty-state elevated">
            <p>{t('myCourses.noEnrolledYet')}</p>
            <Link to="/courses" className="btn-primary">
              {t('myCourses.browseCourses')}
            </Link>
          </div>
        ) : (
          <div className="my-courses-grid">
            {courses.map((course) => {
              const progressPercentage =
                course.total_videos > 0
                  ? Math.round((course.completed_videos / course.total_videos) * 100)
                  : 0;
              const isCompleted = Boolean(course.completed_at);

              return (
                <article
                  key={course.id}
                  className={`enrolled-card ${isCompleted ? 'completed' : ''}`}
                >
                  <div className="card-top">
                    <span className="card-chip">
                      {course.category_name || 'Course'}
                    </span>
                    {isCompleted && <span className="status-pill">{t('myCourses.completed')}</span>}
                  </div>

                  <h3>{course.title}</h3>
                  <p className="card-instructor">{t('myCourses.withInstructor')} {course.instructor_name}</p>

                  <div className="card-progress">
                    <div className="progress-track">
                      <div
                        className="progress-fill"
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                    <span className="progress-value">{progressPercentage}%</span>
                  </div>
                  <p className="progress-details">
                    {t('myCourses.lessonsOf', { completed: course.completed_videos, total: course.total_videos })}
                  </p>

                  <p className="enrollment-date">
                    {t('myCourses.enrolledOn')} {new Date(course.enrolled_at).toLocaleDateString()}
                  </p>

                  <div className="card-actions">
                    <Link
                      to={`/courses/${course.course_id}`}
                      className="btn-primary"
                    >
                      {isCompleted ? t('myCourses.reviewCourse') : t('myCourses.continueLearning')}
                    </Link>
                    <button
                      onClick={() => handleUnenroll(course.course_id)}
                      className="btn-ghost danger"
                    >
                      {t('myCourses.unenroll')}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCourses;
